import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useRouteMatch, Route, Switch } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { useAPIError } from '../../../context';
import { apiAuth } from '../../../utils/hooks';
import { spaces } from '../../../utils/constants';
import { Error404 } from '../..';
import { AdminUserBreadCrumb, AdminUserTitle } from '../shared';
import AdminUserDetailsCards from './AdminUserDetailsCards';
import AdminUserSpaceBilling from './AdminUserSpaceBilling';
import AdminUserDonations from './AdminUserDonations.js';

const AdminUserDetails = ({ t }) => {
    const { addError } = useAPIError();
    const { accountId } = useParams();
    const { path } = useRouteMatch();

    const [isLoading, setIsLoading] = useState(true);
    const [accountData, setAccountData] = useState();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    /**
     * GET request to API to get user's saccount informations
     * @returns {object} - object with user's infos for account and each space
     */
    const getAccountData = useCallback(() => {
        apiAuth()
            .get(`/account/${accountId}`)
            .then((res) => {
                isMounted.current && setAccountData(res.data);
            })
            .catch((error) => {
                const { detail, status } = error?.response?.data;
                addError(detail, status);
            })
            .finally(() => isMounted.current && setIsLoading(false));
    }, [addError, accountId]);

    useEffect(() => {
        isMounted.current = true;

        getAccountData();

        return () => {
            isMounted.current = false;
        };
    }, [getAccountData]);

    /**
     * @description to check if space is signed with SEPA
     * @param {string} mode - charity | climate
     */
    const checkOtherSpaceBilling = (mode) => {
        return mode === spaces.charity
            ? accountData?.charityBilling?.status === 'SIGNED'
            : accountData?.climateBilling?.status === 'SIGNED';
    };

    return (
        <>
            <Helmet>
                <title>
                    {t('admin.user.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <AdminUserBreadCrumb accountId={accountId} />

            <AdminUserTitle
                organisationName={accountData?.organisationName}
                accountId={accountId}
                isLoading={isLoading}
            />

            <Switch>
                <Route exact path={`${path}`}>
                    <AdminUserDetailsCards
                        setIsLoading={setIsLoading}
                        accountData={accountData}
                        setAccountData={setAccountData}
                        isLoading={isLoading}
                        getAccountData={getAccountData}
                    />
                </Route>

                <Route exact path={`${path}/climate-billing`}>
                    <AdminUserSpaceBilling
                        space={spaces.climate}
                        organisationName={accountData?.organisationName}
                        hasOtherSpaceBilling={checkOtherSpaceBilling(
                            spaces.charity,
                        )}
                    />
                </Route>

                <Route exact path={`${path}/charity-billing`}>
                    <AdminUserSpaceBilling
                        space={spaces.charity}
                        organisationName={accountData?.organisationName}
                        hasOtherSpaceBilling={checkOtherSpaceBilling(
                            spaces.climate,
                        )}
                    />
                </Route>

                <Route exact path={`${path}/charity-donations`}>
                    <AdminUserDonations accountId={accountId} />
                </Route>

                <Route component={Error404} />
            </Switch>
        </>
    );
};

export default withTranslation('common')(AdminUserDetails);
